// Core packages

// Third party packages

// Custom packages

/**
 * Keys of the "Rete idrica sanitaria" documents owed because of the sanitary
 * water meters (D.Lgs. 18/23): ND1 control contract, ND2 potability
 * inspection, ND25 legionella inspection, ND26 legionella risk assessment.
 *
 * They are property level documents: their rows carry no meter, so the meters
 * they refer to are derived from the mapping with the same rule the back-end
 * uses to make them mandatory.
 *
 * @since 3.0.0
 */
export const SANITARY_WATER_NETWORK_DOC_KEYS: ReadonlyArray<string> = [
  "ND1",
  "ND2",
  "ND25",
  "ND26",
];

/**
 * Option stored by the water meter field "Tipo di utilizzo"
 * (`tipodiutilizzo5`) for "Idrico sanitario". The mapping form stores select
 * options by position (`option-N`), so this is the back-end literal itself:
 * `SANITARY_WATER_USAGE` in `src/api/v2/utils/document-archive-obligations.util.ts`
 * and the guard in `src/utils/condo-structure-activation.ts`. Keep the three
 * in sync.
 */
const SANITARY_WATER_USAGE = "option-0";

/**
 * The only fields read from a `contatoriAcqua` element of
 * GET condo-structures/:id. Every value comes from the mapping form, so none
 * of them is trusted to have the expected type.
 */
interface MappedWaterMeter {
  nome?: unknown;
  active?: unknown;
  tipodiutilizzo5?: unknown;
}

/**
 * A node of the tree built by `DocumentTypeStructure.getDocumentArchiveData`:
 * only the document rows carry a `key`, every level may carry a `content`
 * array.
 */
interface ArchiveTreeNode {
  key?: unknown;
  content?: unknown;
  structureTitle?: unknown;
}

/**
 * Same rule as the back-end: a meter without `active` counts as active, any
 * other falsy value means deactivated.
 */
function isActiveMeter(meter: MappedWaterMeter): boolean {
  return typeof meter.active === "undefined" || Boolean(meter.active);
}

/**
 * "Tipo di utilizzo" arrives either as a single string or as a multi-select
 * array.
 */
function selectedUsages(stored: unknown): unknown[] {
  if (Array.isArray(stored)) {
    return stored;
  }
  if (typeof stored === "string" && stored !== "") {
    return [stored];
  }
  return [];
}

/**
 * Names of the active water meters that declare the sanitary usage, in
 * mapping order and separated by a comma: the meters ND1, ND2, ND25 and ND26
 * refer to.
 *
 * Meters without a name are left out instead of printing an empty entry, and
 * two meters with the same name are both listed, one entry per meter.
 * Returns an empty string when no meter matches or when the mapping is not
 * loaded.
 *
 * @since 3.0.0
 *
 * @param condoStructure mapping of the property, GET condo-structures/:id
 */
export function getSanitaryWaterMeterNames(condoStructure: unknown): string {
  if (!condoStructure || typeof condoStructure !== "object") {
    return "";
  }
  const meters = (condoStructure as { contatoriAcqua?: unknown }).contatoriAcqua;
  if (!Array.isArray(meters)) {
    return "";
  }
  const names: string[] = [];
  meters.forEach((meter: unknown) => {
    if (!meter || typeof meter !== "object") {
      return;
    }
    const waterMeter = meter as MappedWaterMeter;
    if (
      !isActiveMeter(waterMeter) ||
      selectedUsages(waterMeter.tipodiutilizzo5).indexOf(SANITARY_WATER_USAGE) === -1
    ) {
      return;
    }
    const name = typeof waterMeter.nome === "string" ? waterMeter.nome.trim() : "";
    if (name) {
      names.push(name);
    }
  });
  return names.join(", ");
}

/**
 * Sets `structureTitle`, which the documents page prints in parentheses after
 * the document type, on every ND1, ND2, ND25 and ND26 row of the archive tree:
 * the rows of the "Rete idrica sanitaria" section and their copies in the
 * aggregated "Contratti" table. The row `title` is left untouched, since it is
 * also used for sorting, by the dialogs and by the print actions.
 *
 * Does nothing when `meterNames` is empty.
 *
 * @since 3.0.0
 *
 * @param docs tree returned by `DocumentTypeStructure.getDocumentArchiveData`
 * @param meterNames output of `getSanitaryWaterMeterNames`
 */
export function applySanitaryWaterMeterNames(docs: unknown, meterNames: string): void {
  if (!meterNames || !Array.isArray(docs)) {
    return;
  }
  const pending: unknown[] = docs.slice();
  while (pending.length) {
    const node = pending.pop();
    if (!node || typeof node !== "object") {
      continue;
    }
    const treeNode = node as ArchiveTreeNode;
    if (
      typeof treeNode.key === "string" &&
      SANITARY_WATER_NETWORK_DOC_KEYS.indexOf(treeNode.key) > -1
    ) {
      treeNode.structureTitle = meterNames;
    }
    if (Array.isArray(treeNode.content)) {
      treeNode.content.forEach((child: unknown) => {
        pending.push(child);
      });
    }
  }
}
